/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Icon from '@material-ui/core/Icon';
import ListItemText from '@material-ui/core/ListItemText';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { Collapse } from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import MuiListItem from '@material-ui/core/ListItem';
import { useTranslation } from 'react-i18next';
import Tooltip from 'components/common/Tooltip';
import { SidebarContext } from './useSidebar';

const useStyles = makeStyles(theme => ({
  listIcon: ({ isExpandedDrawer }) => ({
    minWidth: '0px',
    paddingRight: isExpandedDrawer ? '.7rem' : '0rem',
    marginLeft: isExpandedDrawer ? '1.1rem' : '1rem',
    backgroundColor: 'inherit',
    color: 'inherit',
  }),
  divider: {
    borderTop: `1px solid ${theme.palette.white}`,
  },
  listItem: ({ isExpandedDrawer, isExpanded }) => ({
    paddingTop: isExpandedDrawer ? '0.5rem' : '.5rem',
    paddingBottom: isExpandedDrawer ? '0.5rem' : '.5rem',
    paddingLeft: isExpandedDrawer ? '.7rem' : '1rem',
    color: isExpanded && theme?.palette?.expandedTextColor ? theme.palette.expandedTextColor : 'inherit',
    fontWeight: 600,
    backgroundColor: isExpanded ? theme.palette.expandedNavBarButton : 'inherit',
    border: 'none',
    '&:visited': {
      color: 'inherit',
    },
    flexWrap: 'wrap',
    justifyContent: 'center',
    height: '3.375rem',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.sidebarItemHoverBackground,
      color: theme.palette.sidebarItemHoverText,
    },
  }),
  subListItem: ({ isExpandedDrawer }) => ({
    backgroundColor: theme.palette.sidebarSubItemBackground,
    color: theme.palette.sidebarSubItemText,
    height: '3.375rem',
    opacity: isExpandedDrawer ? '1' : '0',
    transition: theme.transitions.create('opacity', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    '&:visited': {
      color: theme.palette.sidebarSubItemText,
    },
    '&:focus-visible': {
      border: `3px solid ${theme.palette.focus}`,
      borderRadius: '4px',
    },
    '&:hover': {
      backgroundColor: theme.palette.sidebarSubItemHoverBackground,
      color: theme.palette.sidebarSubItemHoverText,
    },
  }),
  collapse: {
    padding: '0',
  },
  textItem: ({ isExpandedDrawer, isMediumOrBigger }) => ({
    fontSize: !isExpandedDrawer && isMediumOrBigger ? '0.7rem' : '',
    textAlign: !isExpandedDrawer && isMediumOrBigger ? 'center' : 'left',
    opacity: isExpandedDrawer ? '1' : '0',
    transition: theme.transitions.create('opacity', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  textContainer: {
    minWidth: 'auto',
    '& .MuiListItemText-primary': {
      fontWeight: 600,
      color: theme.palette.expandedNavBarButtonText ? theme.palette.expandedNavBarButtonText : 'inherit',
    },
  },
  subTextContainer: {
    marginLeft: '1rem',
  },
  highlight: {
    background: theme.palette.sidebarHighlightBackground,
    width: '.25rem',
    height: '100%',
    position: 'absolute',
    top: 0,
    right: 0,
  },
  expandMoreIcon: {
    transform: 'rotate(-90deg)',
  },
}));

const ListItem = withStyles(theme => ({
  root: {
    '&$selected': {
      boxShadow: 'none !important',
      backgroundColor: theme.palette.activeNavBarButtonBackground,
      color: theme.palette.activeNavBarButtonText,
    },
    '&$selected:hover': {
      backgroundColor: theme.palette.sidebarItemSelectedBackgroundHover,
      color: theme.palette.hover,
    },

    '&[data-focustype="key"]': {
      marginLeft: ({ type }) => (type === 'list' ? '0.3rem' : '0rem'),
      width: ({ type }) => (type === 'list' ? 'calc(100% - 0.58rem)' : '100%'),
      flexWrap: 'nowrap',
      outline: 'none',
      boxShadow: ({ type }) =>
        type === 'list'
          ? `inset 0 0 0 2px ${theme.palette.sidebarFocusBorder}, 0 0 0 4px ${theme.palette.sidebarFocus} !important`
          : 'none !important',

      '& > span': {
        display: 'none',
      },
    },
  },
  selected: {},
}))(MuiListItem);

export default function ListItemSidebar({ sidebarItem, indexID }) {
  const { t } = useTranslation();

  const isItemSubList = !!sidebarItem.subList;

  const { isSelected, isExpandedDrawer, handleItemClick, buttonExpanded, isMediumOrBigger, navRef } = useContext(
    SidebarContext,
  );

  const isExpanded = buttonExpanded === sidebarItem.text;
  const classes = useStyles({ isExpandedDrawer, isMediumOrBigger, isExpanded });

  // make the toolTip controlled Component by ListItem
  const [tooltipOpen, setTooltipOpen] = useState(false);

  return (
    <>
      <Tooltip
        className="body-row-tooltip"
        title={t(sidebarItem.text)}
        placement="right"
        arrow
        enterDelay={500}
        enterNextDelay={500}
        disableHoverListener={!!isExpandedDrawer}
        open={tooltipOpen}
        onOpen={() => setTooltipOpen(true)}
        onClose={() => setTooltipOpen(false)}
      >
        <ListItem
          className={classes.listItem}
          type="list"
          component={sidebarItem.link ? Link : 'button'}
          to={sidebarItem.link}
          onClick={handleItemClick(sidebarItem, isItemSubList)}
          selected={isSelected(sidebarItem)}
          ref={indexID === 0 ? navRef : null}
          onMouseLeave={() => setTooltipOpen(false)}
          aria-label={t(sidebarItem.text)}
        >
          <ListItemIcon className={classes.listIcon}>
            <Icon>{sidebarItem.icon}</Icon>
          </ListItemIcon>
          <ListItemText
            primary={isExpandedDrawer && t(sidebarItem.text)}
            classes={{ primary: classes.textItem, root: classes.textContainer }}
          />
          {sidebarItem.subList && (
            <>{!isExpanded ? <ExpandMoreIcon className={classes.expandMoreIcon} /> : <ExpandLessIcon />}</>
          )}
          {isSelected(sidebarItem) && <span className={classes.highlight} aria-hidden="true" />}
        </ListItem>
      </Tooltip>
      {sidebarItem.subList && (
        <Collapse
          component="ul"
          in={isExpanded}
          collapsedHeight={0}
          className={classes.collapse}
          timeout="auto"
          unmountOnExit
        >
          {sidebarItem.subList?.map(subListItem => (
            <ListItem
              className={classes.subListItem}
              type="sublist"
              component={Link}
              key={subListItem.text}
              to={subListItem.link}
            >
              <ListItemText primary={t(subListItem.text)} className={classes.subTextContainer} />
            </ListItem>
          ))}
        </Collapse>
      )}
    </>
  );
}
